import { getISODay, parseISO } from 'date-fns'
import { ChevronRight, Clock, Lock, Palmtree } from 'lucide-react'
import type { ReactNode } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { formatHmm } from '@/lib/hours'
import { useTeamDay } from '@/lib/time-queries'
import { readDescription } from '@/lib/timesheet-copy'
import type { TeamDayEntry, TeamPersonDay } from '@/lib/types'
import { cn } from '@/lib/utils'
import { dayStatus, isoWeekId, weekMetaFromId } from '@/lib/week'
import { DayNav, DayTotalsStrip } from './DayNav'
import { RowSep } from './RowLabel'
import { StoplichtDot } from './StoplichtDot'
import { ApprovalGlyph } from './TeamApproval'
import { TimesheetToolbar } from './TimesheetToolbar'

interface TeamDayViewProps {
  date: string
  onDateChange: (date: string) => void
  onOpenPersonWeek: (personId: string) => void
  highlightPersonId?: string
  viewControl: ReactNode
}

const COLUMNS = 4

function lockTooltip(entry: TeamDayEntry): string | null {
  if (!entry.locked) return null
  return entry.invoiceNumber ? `Gefactureerd op ${entry.invoiceNumber}` : 'Gefactureerd'
}

/** Eerste regel van een klantblok binnen een persoon; alleen daar staat de klantnaam. */
function firstOfClient(entries: TeamDayEntry[], index: number): boolean {
  return index === 0 || entries[index - 1].clientId !== entries[index].clientId
}

function freeLabel(person: TeamPersonDay): string {
  if (person.dayLabel) return person.dayLabel
  return person.dayType === 'vacation' ? 'Vakantie' : 'Vrije dag'
}

/** Zelfde grammatica als `RowLabel`, maar zonder de bewerk-slots en de WeekRow-typering. */
function EntryLabel({ entry, first, stacked }: { entry: TeamDayEntry; first: boolean; stacked?: boolean }) {
  const project = <span className="truncate text-ink-faint">{entry.projectName}</span>
  const category = entry.categoryName ? <span className="truncate text-ink-faint">{entry.categoryName}</span> : null

  if (stacked) {
    return (
      <span className="block min-w-0">
        {first ? <span className="block truncate font-medium text-ink">{entry.clientName}</span> : null}
        <span className={cn('flex min-w-0 items-center gap-1 text-sm', !first && 'pl-2')}>
          {project}
          {category ? (
            <>
              <RowSep />
              {category}
            </>
          ) : null}
        </span>
      </span>
    )
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', !first && 'pl-3')}>
      {first ? (
        <>
          <span className="truncate font-medium text-ink">{entry.clientName}</span>
          <RowSep />
        </>
      ) : null}
      {project}
      {category ? (
        <>
          <RowSep />
          {category}
        </>
      ) : null}
    </span>
  )
}

/**
 * Wat het team op één dag deed, op regelniveau. Volledig alleen-lezen: elk pad naar
 * bewerken loopt via de week van die persoon, zodat er geen tweede invoeroppervlak
 * ontstaat. Declarabiliteit staat er bewust niet bij; dat is een periodemaat.
 */
export function TeamDayView({ date, onDateChange, onOpenPersonWeek, highlightPersonId, viewControl }: TeamDayViewProps) {
  const dayQuery = useTeamDay(date)
  const parsed = parseISO(date)
  const weekId = isoWeekId(parsed)
  const meta = weekMetaFromId(weekId)
  const dayIndex = getISODay(parsed) - 1
  const day = meta.days[dayIndex]

  const data = dayQuery.data
  const persons = data?.persons ?? []
  const booked = persons.filter((person) => person.entries.length > 0)
  const missing = persons.filter(
    (person) => person.weeklyHoursRequired && person.expectedWorkday && person.entries.length === 0,
  )
  // Alleen een persoonlijke vrije dag; dat het weekend is zegt de dagnavigatie zelf al.
  const free = persons.filter(
    (person) =>
      person.entries.length === 0 &&
      !person.expectedWorkday &&
      (person.dayType === 'vacation' || person.dayType === 'holiday'),
  )

  function goToDay(targetWeekId: string, index: number) {
    onDateChange(weekMetaFromId(targetWeekId).days[index].iso)
  }

  const readoutCount = data
    ? `${data.totals.personsWithEntries} van ${data.totals.personCount} geboekt · ${formatHmm(toNumber(data.totals.dayTotal))} u`
    : null

  return (
    <div className="space-y-4">
      <TimesheetToolbar
        nav={
          <>
            <DayNav weekId={weekId} dayIndex={dayIndex} onChange={goToDay} />
            {viewControl}
          </>
        }
        readout={
          <>
            {/* Op een telefoon stapelt de balk toch al, dus daar past de teller prima en
                is hij het enige wat de dag samenvat. Hij wijkt alleen in de band waar de
                balk anders omklapt: vanaf sm tot de sidebar-breedte waar alles weer past. */}
            {readoutCount ? (
              <span className="text-sm text-ink-faint sm:hidden min-[1120px]:inline">{readoutCount}</span>
            ) : null}
            {/* Een kloppend skelet naast een foutmelding belooft iets dat niet meer komt. */}
            {!readoutCount && !dayQuery.isError ? (
              <Skeleton className="h-4 w-40 sm:hidden min-[1120px]:block" />
            ) : null}
            {/* De staafjes zijn 12px breed en dragen geen label: op een telefoon lezen ze
                als vegen in plaats van als grafiek, en tikken lukt er nauwelijks op. Daar
                doet de dagstepper het werk. */}
            {data?.weekDailyTotals.length ? (
              <div className="hidden sm:block">
                <DayTotalsStrip
                  weekId={weekId}
                  totals={data.weekDailyTotals}
                  dayIndex={dayIndex}
                  onSelect={(index) => goToDay(weekId, index)}
                />
              </div>
            ) : null}
          </>
        }
      />

      {dayQuery.isError ? (
        <ErrorState compact error={dayQuery.error} onRetry={() => dayQuery.refetch()} />
      ) : dayQuery.isLoading ? (
        <LoadingBlocks />
      ) : booked.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={`Niemand boekte uren op ${day.label}`}
          description="Zodra iemand uren boekt op deze dag verschijnen ze hier."
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-card bg-surface shadow-card sm:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-hairline">
                    <th className="label-caps px-4 py-3 text-left">Klant · Project · Categorie</th>
                    <th className="label-caps w-24 px-2 py-3 text-right">Uren</th>
                    <th className="label-caps w-full px-3 py-3 text-left">Omschrijving</th>
                    <th className="label-caps w-28 px-2 py-3 text-right">Reiskosten</th>
                  </tr>
                </thead>
                <tbody>
                  {booked.map((person, personIdx) => (
                    <PersonRows
                      key={person.personId}
                      person={person}
                      blockStart={personIdx > 0}
                      highlighted={person.personId === highlightPersonId}
                      onOpenWeek={() => onOpenPersonWeek(person.personId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <ul className="space-y-2 sm:hidden">
            {booked.map((person) => (
              <PersonCard
                key={person.personId}
                person={person}
                highlighted={person.personId === highlightPersonId}
                onOpenWeek={() => onOpenPersonWeek(person.personId)}
              />
            ))}
          </ul>
        </>
      )}

      {dayQuery.isLoading || dayQuery.isError ? null : (
        <GapStrip missing={missing} free={free} bookedCount={booked.length} onOpenPersonWeek={onOpenPersonWeek} />
      )}
    </div>
  )
}

interface PersonRowsProps {
  person: TeamPersonDay
  blockStart: boolean
  highlighted: boolean
  onOpenWeek: () => void
}

function PersonRows({ person, blockStart, highlighted, onOpenWeek }: PersonRowsProps) {
  const total = toNumber(person.dayTotal)

  return (
    <>
      <tr className={cn('border-b border-hairline', blockStart && 'border-t border-hairline-strong')}>
        <td colSpan={COLUMNS} className={cn('p-0', highlighted ? 'bg-accent-600/5' : 'bg-fill-1')}>
          <button
            type="button"
            onClick={onOpenWeek}
            className="flex w-full items-center gap-1.5 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-fill-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500/40"
          >
            {/* De actie staat naast de inhoud en niet als aria-label erover: dat laatste
                zou naam, akkoord en dagtotaal uit de voorgelezen naam duwen. */}
            <span className="sr-only">Week van {person.displayName} openen</span>
            <ApprovalGlyph state={person.approval.state} className="shrink-0" />
            <span className="truncate font-medium text-ink">{person.displayName}</span>
            <ChevronRight className="mr-2 h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
            {/* Bij de naam en niet tegen de rechterrand: de balk overspant alle kolommen,
                dus rechts uitgelijnd zou een urensom onder de kop Reiskosten belanden. */}
            <StoplichtDot status={dayStatus(total, person.expectedWorkday)} className="shrink-0" />
            <span className="num shrink-0 font-semibold text-ink">{formatHmm(total)}</span>
          </button>
        </td>
      </tr>
      {person.entries.map((entry, index) => {
        const hours = toNumber(entry.hours)
        const travel = toNumber(entry.travelAmount)
        const description = readDescription(entry.description, hours)
        return (
          <tr key={entry.entryId} className="border-b border-hairline">
            <td className="whitespace-nowrap px-4 py-2.5">
              <EntryLabel entry={entry} first={firstOfClient(person.entries, index)} />
            </td>
            <td className="px-2 py-2.5 text-right">
              <Tooltip content={lockTooltip(entry)}>
                <span className={cn('inline-flex items-center gap-1', hours > 0 ? 'text-ink' : 'text-ink-faint')}>
                  {entry.locked ? <Lock className="h-3 w-3 text-ink-faint" /> : null}
                  <span className="num text-sm">{formatHmm(hours)}</span>
                </span>
              </Tooltip>
            </td>
            <td className={cn('px-3 py-2.5 text-sm', description.missing ? 'text-ink-faint/70' : 'text-ink-soft')}>
              {description.text}
            </td>
            <td className="money px-2 py-2.5 text-right text-sm text-ink-faint">
              {travel > 0 ? formatEuros(travel) : ''}
            </td>
          </tr>
        )
      })}
    </>
  )
}

interface PersonCardProps {
  person: TeamPersonDay
  highlighted: boolean
  onOpenWeek: () => void
}

/**
 * De mobiele tegenhanger van een persoonsblok: dezelfde kaartgrammatica als de eigen
 * daglijst, maar zonder actiestrip en zonder tap-popover; bewerken loopt via de week.
 */
function PersonCard({ person, highlighted, onOpenWeek }: PersonCardProps) {
  const total = toNumber(person.dayTotal)

  return (
    <li
      className={cn(
        'overflow-hidden rounded-card border border-hairline bg-surface shadow-card',
        highlighted && 'border-accent-600/40 bg-accent-600/5',
      )}
    >
      <button
        type="button"
        onClick={onOpenWeek}
        className="flex w-full items-center gap-1.5 border-b border-hairline px-4 py-2.5 text-left transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500/40 active:bg-accent-600/5"
      >
        <span className="sr-only">Week van {person.displayName} openen</span>
        <ApprovalGlyph state={person.approval.state} className="shrink-0" />
        <span className="truncate font-medium text-ink">{person.displayName}</span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <StoplichtDot status={dayStatus(total, person.expectedWorkday)} />
          <span className="num text-sm font-semibold text-ink">{formatHmm(total)}</span>
        </span>
      </button>
      <ul className="divide-y divide-hairline">
        {person.entries.map((entry, index) => {
          const hours = toNumber(entry.hours)
          const travel = toNumber(entry.travelAmount)
          const description = readDescription(entry.description, hours)
          return (
            <li key={entry.entryId} className="flex flex-col gap-1.5 px-4 py-3">
              <span className="flex items-start justify-between gap-3">
                <EntryLabel entry={entry} first={firstOfClient(person.entries, index)} stacked />
                <span className="flex shrink-0 items-center gap-1.5">
                  {entry.locked ? <Lock className="h-3 w-3 text-ink-faint" aria-hidden="true" /> : null}
                  <span className={cn('num text-base', hours > 0 ? 'font-medium text-ink' : 'text-ink-faint')}>
                    {formatHmm(hours)}
                  </span>
                </span>
              </span>
              <span className="flex items-end justify-between gap-3">
                <span className={cn('min-w-0 flex-1 text-sm', description.missing ? 'text-ink-faint/70' : 'text-ink-soft')}>
                  {description.text}
                </span>
                {travel > 0 ? (
                  <span className="money shrink-0 rounded-pill bg-fill-2 px-2 py-0.5 text-xs font-medium text-ink-soft">
                    {formatEuros(travel)}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

interface GapStripProps {
  missing: TeamPersonDay[]
  free: TeamPersonDay[]
  bookedCount: number
  onOpenPersonWeek: (personId: string) => void
}

/**
 * Onder de lijst en niet erboven: op de dag zelf is een gat normaal, dus dit is een
 * mededeling en geen alarm.
 */
function GapStrip({ missing, free, bookedCount, onOpenPersonWeek }: GapStripProps) {
  if (missing.length === 0 && free.length === 0) {
    if (bookedCount === 0) return null
    return <p className="px-1 text-sm text-ink-faint">Iedereen heeft geboekt</p>
  }

  // Is iedereen die niets boekte om dezelfde reden vrij, dan is dat een eigenschap van
  // de dag en niet van de personen; de namen erbij zetten is dan een lijst van iedereen.
  const labels = new Set(free.map(freeLabel))
  const collective = missing.length === 0 && free.length > 1 && labels.size === 1
  if (collective) {
    return (
      <p className="flex items-center gap-2 px-1 text-sm text-ink-faint">
        <Palmtree className="h-4 w-4" aria-hidden="true" />
        Vrij · {freeLabel(free[0])}
      </p>
    )
  }

  return (
    // Twee kolommen zodat de namen onder elkaar uitkomen; onder sm stapelen ze,
    // want daar is voor twee kolommen geen ruimte.
    <div className="grid gap-x-4 gap-y-1.5 rounded-card border border-hairline bg-fill-1 px-4 py-3 text-sm sm:grid-cols-[max-content_1fr]">
      {missing.length > 0 ? (
        <>
          <span className="flex items-center gap-2 text-ink-soft">
            <StoplichtDot status="empty" />
            Nog niets geboekt ({missing.length})
          </span>
          <PersonLinks persons={missing} onOpenPersonWeek={onOpenPersonWeek} />
        </>
      ) : null}
      {free.length > 0 ? (
        <>
          <span className="flex items-center gap-2 text-ink-soft">
            <Palmtree className="h-4 w-4 text-ink-faint" aria-hidden="true" />
            Vrij
          </span>
          <PersonLinks persons={free} withFreeLabel onOpenPersonWeek={onOpenPersonWeek} />
        </>
      ) : null}
    </div>
  )
}

interface PersonLinksProps {
  persons: TeamPersonDay[]
  withFreeLabel?: boolean
  onOpenPersonWeek: (personId: string) => void
}

function PersonLinks({ persons, withFreeLabel, onOpenPersonWeek }: PersonLinksProps) {
  return (
    <span className="flex flex-wrap items-center gap-x-1.5">
      {persons.map((person, index) => (
        // Het scheidingsteken hangt achter het item en niet ervoor, anders wrapt het mee
        // naar de volgende regel en begint die met een losse middot.
        <span key={person.personId} className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOpenPersonWeek(person.personId)}
            aria-label={`Week van ${person.displayName} openen`}
            className="rounded-sm py-1 text-ink-soft underline-offset-2 transition-colors duration-150 hover:text-accent-700 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
          >
            {person.displayName}
          </button>
          {withFreeLabel ? <span className="text-ink-faint">({freeLabel(person).toLowerCase()})</span> : null}
          {index < persons.length - 1 ? <RowSep /> : null}
        </span>
      ))}
    </span>
  )
}

function LoadingBlocks() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 2 }).map((_, block) => (
        <div key={block} className="overflow-hidden rounded-card bg-surface shadow-card">
          <div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-10" />
          </div>
          {Array.from({ length: 2 }).map((_, row) => (
            <div key={row} className="flex items-center gap-4 border-b border-hairline px-4 py-3 last:border-b-0">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
